import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from 'components/Anyfit/common/Layout';
import styled, { css } from 'styled-components';
import { useAlert } from 'contexts/AlertContext';
import { palette } from 'pages/AnyfitMall/theme';
import { API_BASE_URL } from './config';
import { jwtDecode } from 'jwt-decode';
import {
  parseTokenFromUrl,
  getTokenFromStorage,
  validateToken,
} from './common';
import { getBranding } from 'utils/branding';

const Container2 = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  padding: 16px;
  // min-width: 330px;
  width: 100%;
  max-width: 500px;
`;

const Title2 = styled.h1`
  font-size: 20px;
  color: black;
  margin-bottom: 24px;

  font-weight: bold;
  font-family: 'Noto Sans KR', sans-serif;
  margin-top: 20px;
`;

const OptionList = styled.ul`
  width: 100%;
  list-style: none;
  padding: 0;
`;

const OptionItem = styled.li`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 8px; // remove margin for the last item
  background-color: #f7f7f7;
  border: 1px solid #e7e7e7;
  width: 100%;

  &:last-child {
    margin-bottom: 0;
  }
`;

const OptionText = styled.span`
  font-size: 16px;
  flex-grow: 1;
  color: #333;
`;

const CheckMark = styled.span`
  display: inline-block;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${({ agreed }) =>
    agreed ? palette.AnyBlue : palette.Grey300};
  margin-right: 16px;
  // Add checkmark styling
  position: relative; // ::after를 위한 위치 설정

  // Add checkmark styling
  ${({ agreed }) =>
    agreed &&
    `
    &::after {
      content: 'v';
      color: white;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 16px;
      font-weight: bold;
    }
  `}
`;

const Button = styled.button`
  background-color: ${({ allAgreed }) =>
    allAgreed ? palette.AnyBlue : palette.Grey300};
  color: white;
  border: none;
  padding: 12px;
  width: 100%;
  border-radius: 4px;
  font-size: 18px;
  margin-top: 16px;
  cursor: ${({ allAgreed }) => (allAgreed ? 'pointer' : 'default')};
  margin-bottom: 30px; // Add some vertical space around the line

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Separator = styled.div`
  height: 1px;
  background-color: ${palette.Grey300}; // Use the grey color you prefer
  margin-top: 24px; // Add some vertical space around the line
  margin-bottom: 30px; // Add some vertical space around the line
`;

const OptionContent = styled.div`
  background-color: #f7f7f7;
  height: 300px;
  width: 100%;
  margin-bottom: 0.8rem;

  max-height: ${(props) => (props.expanded ? '300px' : '0')};
  overflow-y: auto;
  transition: max-height 0.3s ease;
`;

const OptionInnerContent = styled.div`
  padding-top: 16px;
  padding-left: 16px;
  padding-right: 16px;
  padding-bottom: 16px;
  margin-bottom: 16px;
`;

const OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const rotateStyles = css`
  transition: transform 0.3s ease-in-out;
  transform: ${(props) =>
    props.direction === 'right' ? 'rotate(90deg)' : 'rotate(0deg)'};
`;

const RotatingImage = styled.img`
  width: 16px; // Set the width as needed
  height: 16px; // Set the height as needed
  margin: auto 0; // Push the image to the right
  ${(props) => props.isRotated && rotateStyles};
`;

// 동의 완료 후 복귀할 URL을 계산한다.
// returnUrl 쿼리가 있으면 (선물 플로우 등) 원래 경로로 복귀, 없으면 기존 유료구매 페이지로.
// 보안상 내부 /anyfit/* 경로만 허용 (open redirect 방지).
const resolveSuccessRedirect = (storageToken) => {
  const params = new URLSearchParams(window.location.search);
  const rawReturnUrl = params.get('returnUrl');
  if (rawReturnUrl) {
    const decoded = decodeURIComponent(rawReturnUrl);
    if (decoded.startsWith('/anyfit/')) {
      const separator = decoded.includes('?') ? '&' : '?';
      return `${decoded}${separator}TOKEN=${storageToken}`;
    }
  }
  return `/anyfit/productDetailV2?TOKEN=${storageToken}`;
};

const AnyfitAgreement = () => {
  const { showAlert } = useAlert();
  const branding = getBranding();
  //submit api
  const handleSubmit = async () => {
    console.log('🚀 [Agreement] 동의 버튼 클릭 - handleSubmit 시작');

    // 1. 전체 동의 여부 확인
    if (!allAgreed) {
      console.log('⚠️ [Agreement] 모든 조항에 동의하지 않음');
      showAlert('모든 조항에 동의해주세요');
      return;
    }

    console.log('✅ [Agreement] 모든 조항 동의 확인');

    // 2. localStorage에서 토큰 가져오기 (스코프 확장)
    const storageToken = localStorage.getItem('token');

    try {
      console.log(
        '📍 [Agreement] localStorage 토큰:',
        storageToken ? `${storageToken.substring(0, 20)}...` : 'null',
      );

      // 3. 토큰 존재 여부 체크 (null 방어)
      if (!storageToken) {
        console.error('❌ [Agreement] 토큰이 localStorage에 없습니다.');
        showAlert('토큰이 유효하지 않습니다. 다시 시도해주세요.');
        navigate('/anyfit/error');
        return;
      }

      // 4. 토큰 디코딩 (try-catch로 안전하게 처리)
      let decodedToken;
      try {
        decodedToken = jwtDecode(storageToken);
        console.log('✅ [Agreement] 토큰 디코딩 성공:', {
          id: decodedToken.id,
          exp: decodedToken.exp
            ? new Date(decodedToken.exp * 1000).toISOString()
            : 'N/A',
        });
      } catch (decodeError) {
        console.error('❌ [Agreement] 토큰 디코딩 실패:', decodeError);
        showAlert('유효하지 않은 토큰입니다.');
        localStorage.removeItem('token');
        navigate('/anyfit/error');
        return;
      }

      // 5. 요청 데이터 준비
      const requestData = {
        personal_info_collection_consent: agreements[2],
        sensitive_info_collection_consent: agreements[4],
        personal_info_third_party_consent: agreements[3],
        sensitive_info_third_party_consent: agreements[5],
      };

      console.log('📤 [Agreement] 동의 API 요청 시작');
      console.log(
        '📤 [Agreement] 요청 URL:',
        `${API_BASE_URL}/api/createConsentAgreements`,
      );
      console.log('📤 [Agreement] 요청 데이터:', requestData);

      // 6. API 요청
      const response = await axios.post(
        `${API_BASE_URL}/api/createConsentAgreements`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${storageToken}`,
          },
        },
      );

      console.log('📥 [Agreement] 서버 응답 전체:', response);
      console.log(
        '📥 [Agreement] 응답 상태:',
        response.status,
        response.statusText,
      );
      console.log('📥 [Agreement] 응답 데이터:', response.data);

      // 7. 응답 처리
      const successRedirect = resolveSuccessRedirect(storageToken);
      if (response.data.isSuccess) {
        console.log('✅ [Agreement] 동의 완료 성공');
        showAlert('동의하였습니다.');
        navigate(successRedirect);
      } else {
        console.log(
          '⚠️ [Agreement] isSuccess가 false지만 200 응답 - 복귀 경로로 이동',
        );
        console.log('⚠️ [Agreement] 응답 상세:', response.data);
        showAlert('상품 페이지로 이동합니다.');
        navigate(successRedirect);
      }
    } catch (error) {
      console.error('❌ [Agreement] 동의 처리 중 에러 발생:', error);

      // 8. Axios 에러 상세 분석
      if (axios.isAxiosError(error)) {
        if (error.response) {
          // 서버가 2xx 외의 상태코드로 응답한 경우
          console.error('📥 [Agreement] 서버 에러 응답:', {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data,
            headers: error.response.headers,
          });

          const errorData = error.response.data;
          const errorMessage =
            errorData?.message || errorData?.error || '알 수 없는 오류';

          // 이미 동의한 경우 처리
          if (
            errorMessage.includes('이미 동의') ||
            errorMessage === 'ALREADY_AGREED' ||
            errorData?.code === 'ALREADY_AGREED'
          ) {
            console.log('⚠️ [Agreement] 이미 동의한 사용자 - 복귀 경로로 이동');
            showAlert('이미 동의하셨습니다. 상품 페이지로 이동합니다.');
            navigate(resolveSuccessRedirect(storageToken));
            return;
          }

          // 인증 실패
          if (error.response.status === 401) {
            console.error('❌ [Agreement] 인증 실패 - 토큰 만료 또는 무효');
            showAlert('인증이 만료되었습니다. 다시 로그인해주세요.');
            localStorage.removeItem('token');
            navigate('/anyfit/error');
            return;
          }

          // 서버 에러
          if (error.response.status >= 500) {
            console.error('❌ [Agreement] 서버 내부 오류');
            showAlert(
              `서버 오류가 발생했습니다. (${error.response.status})\n잠시 후 다시 시도해주세요.`,
            );
            return;
          }

          // 기타 에러
          showAlert(`동의 처리 실패: ${errorMessage}`);
        } else if (error.request) {
          // 요청은 보냈지만 응답을 받지 못한 경우
          console.error('❌ [Agreement] 서버 응답 없음:', error.request);
          showAlert('서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.');
        } else {
          // 요청 설정 중 에러 발생
          console.error('❌ [Agreement] 요청 설정 에러:', error.message);
          showAlert('요청 처리 중 오류가 발생했습니다.');
        }
      } else {
        // Axios 에러가 아닌 일반 에러
        console.error('❌ [Agreement] 일반 에러:', error);
        showAlert('동의에 실패하였습니다.');
      }
    }
  };

  // 상태관리
  const navigate = useNavigate();
  const [isReady, setIsReady] = useState(false);
  const [allAgreed, setAllAgreed] = useState(false);
  const [agreements, setAgreements] = useState({
    2: false,
    3: false,
    4: false,
    5: false,
  });

  const handleAllAgree = () => {
    const newState = !allAgreed;
    setAllAgreed(newState);
    setAgreements({
      2: newState,
      3: newState,
      4: newState,
      5: newState,
    });
  };

  // 토큰 초기화 useEffect
  useEffect(() => {
    let cancelled = false;

    const initializeToken = async () => {
      console.log('🔍 [Agreement] 토큰 초기화 시작');

      // 1. URL → localStorage 순으로 토큰 확보 (로컬 만료 체크는 fast-path)
      let token = parseTokenFromUrl();
      console.log('📍 [Agreement] URL 파라미터 토큰:', token ? '존재' : '없음');

      if (token) {
        localStorage.setItem('token', token);
        console.log('✅ [Agreement] URL 토큰을 localStorage에 저장 완료');

        try {
          const decoded = jwtDecode(token);
          console.log('✅ [Agreement] URL 토큰 디코딩 성공:', {
            id: decoded.id,
            exp: decoded.exp
              ? new Date(decoded.exp * 1000).toISOString()
              : 'N/A',
          });
        } catch (err) {
          console.error('❌ [Agreement] URL 토큰 디코딩 실패:', err);
          showAlert('유효하지 않은 토큰입니다.');
          localStorage.removeItem('token');
          navigate('/anyfit/error');
          return;
        }
      } else {
        token = getTokenFromStorage();
        console.log(
          '📍 [Agreement] localStorage 토큰:',
          token ? '존재' : '없음',
        );

        if (!token) {
          console.error('❌ [Agreement] 토큰이 존재하지 않습니다.');
          showAlert('유효하지 않은 접근입니다. 다시 시도해주세요.');
          navigate('/anyfit/error');
          return;
        }

        try {
          const decoded = jwtDecode(token);
          console.log('✅ [Agreement] localStorage 토큰 디코딩 성공:', {
            id: decoded.id,
            exp: decoded.exp
              ? new Date(decoded.exp * 1000).toISOString()
              : 'N/A',
          });

          if (decoded.exp && decoded.exp * 1000 < Date.now()) {
            console.error('❌ [Agreement] 토큰이 만료되었습니다.');
            showAlert('토큰이 만료되었습니다. 다시 로그인해주세요.');
            localStorage.removeItem('token');
            navigate('/anyfit/error');
            return;
          }
        } catch (err) {
          console.error('❌ [Agreement] localStorage 토큰 디코딩 실패:', err);
          showAlert('유효하지 않은 토큰입니다.');
          localStorage.removeItem('token');
          navigate('/anyfit/error');
          return;
        }
      }

      // 2. 서버에 동의 상태 확인 — 이미 동의했으면 즉시 redirect
      console.log('📤 [Agreement] validateToken 호출 (사전 차단 게이트)');
      const result = await validateToken(token);
      if (cancelled) return;

      console.log('📥 [Agreement] validateToken 결과:', result);

      if (result.result) {
        // 이미 동의 완료 — 진입 자체를 차단
        console.log(
          '⛔ [Agreement] 이미 동의 완료 — 복귀 경로로 즉시 redirect',
        );
        navigate(resolveSuccessRedirect(result.token), { replace: true });
        return;
      }

      if (result.code === 1) {
        // 동의 필요 — 폼 노출
        console.log('✅ [Agreement] 동의 필요 — 폼 표시');
        setIsReady(true);
        return;
      }

      // 그 외 — 토큰 무효 등 (validateToken이 catch에서 강제 이동하지 않은 케이스)
      console.error('❌ [Agreement] validateToken 실패 — error 페이지로 이동');
      showAlert('유효하지 않은 접근입니다. 다시 시도해주세요.');
      navigate('/anyfit/error');
    };

    initializeToken();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleAgreementChange = (id) => {
    const updatedAgreements = { ...agreements, [id]: !agreements[id] };
    setAgreements(updatedAgreements);
    // Check if all individual agreements are true
    setAllAgreed(Object.values(updatedAgreements).every((v) => v));
  };

  // Dummy options data
  const options = [
    { id: 2, text: '[필수] 개인정보 수집이용 동의' },
    { id: 3, text: '[필수] 개인정보 제3자제공 동의 ' },
    { id: 4, text: '[필수] 민감정보 수집이용 동의 ' },
    { id: 5, text: '[필수] 민감정보 제3자제공 동의 ' },
  ];
  const renderContent = (id) => {
    switch (id) {
      case 2:
        return (
          // add margin
          <OptionContent expanded={expandedItems[id]}>
            <OptionInnerContent>
              □ 개인정보 수집·이용 동의 <br />
              <br />
              - 수집 및 이용 목적: 본인 확인, 서비스 제공, 회원 관리, 민원 처리,
              건강상태 확인, 맞춤형 건강관리 서비스 제공
              <br />
              - 수집하는 정보 항목: 성명, 생년월일, 이메일 주소, 애니핏 회원ID,
              상품 구매·신청·환불 정보
              <br />
              - 보유 및 이용 기간: 회원 탈퇴 시까지 보유하며, 관련 법령에 따른
              보관 기간이 남아있는 경우 해당 기간 동안 보관
              <br />
              - 동의를 거부할 권리 및 불이익: 본 동의를 거부할 권리가 있으며,
              거부할 경우 서비스 이용에 제한이 있을 수 있습니다.
              <br />
              <br />본 동의는 슈가핏 서비스를 이용하는 데 필요한 필수 사항으로,
              개인정보의 수집 및 이용에 대해 동의하시는 경우 아래 확인란에
              체크해 주시기 바랍니다.
            </OptionInnerContent>
          </OptionContent>
        );
      case 3:
        return (
          <OptionContent expanded={expandedItems[id]}>
            <OptionInnerContent>
              □ 개인정보 제3자 제공 동의 <br />
              <br />
              - 정보를 제공받는 자: (주)삼성화재해상보험
              <br />
              <br />
              - 제공받는 자의 이용 목적:
              <br />ㆍ{branding.nameCompact} 슈가핏 서비스 제공 및 개선
              <br />ㆍ{branding.nameCompact} 기본 서비스(건강관리 프로그램 등)
              제공 및 개선
              <br />
              ㆍ신규 서비스 개발 및 통계 분석
              <br />
              ㆍ고객 문의 응대 및 문제 해결
              <br />
              <br />
              - 제공하는 정보 항목: 애니핏 회원ID, 상품 구매·신청·환불 정보
              <br />- 제공받는 자의 보유 및 이용 기간: {
                branding.nameCompact
              }{' '}
              서비스 해지 시까지 보유하며, 관련 법령에 따른 보관 기간이 남아있는
              경우 해당 기간 동안 보관
              <br />
              - 동의를 거부할 권리 및 불이익: 본 동의를 거부할 권리가 있으며,
              거부할 경우 서비스 이용에 제한이 있을 수 있습니다.
              <br />
              <br />본 동의는 슈가핏 서비스를 이용하는 데 필요한 필수 사항으로,
              개인정보의 제3자 제공에 대해 동의하시는 경우 아래 확인란에 체크해
              주시기 바랍니다.
            </OptionInnerContent>
          </OptionContent>
        );
      case 4:
        return (
          <OptionContent expanded={expandedItems[id]}>
            <OptionInnerContent>
              □ 민감정보 수집·이용 동의 <br />
              <br />
              - 수집 및 이용 목적: 건강 상태 확인, 맞춤형 건강관리 서비스 제공
              <br />
              - 수집하는 정보 항목: 혈당 수치, 측정 시간
              <br />
              - 보유 및 이용 기간: 회원 탈퇴 시까지 보유하며, 관련 법령에 따른
              보관 기간이 남아있는 경우 해당 기간 동안 보관
              <br />
              - 동의를 거부할 권리 및 불이익: 본 동의를 거부할 권리가 있으며,
              거부할 경우 서비스 이용에 제한이 있을 수 있습니다.
              <br />
              <br />본 동의는 슈가핏 서비스를 이용하는 데 필요한 필수 사항으로,
              민감정보의 수집 및 이용에 대해 동의하시는 경우 아래 확인란에
              체크해 주시기 바랍니다.
            </OptionInnerContent>
          </OptionContent>
        );
      case 5:
        return (
          <OptionContent expanded={expandedItems[id]}>
            <OptionInnerContent>
              □ 민감정보 제3자 제공 동의 <br />
              <br />
              - 정보를 제공받는 자: (주)삼성화재해상보험
              <br />
              <br />
              - 제공받는 자의 이용 목적:
              <br />ㆍ{branding.nameCompact} 슈가핏 서비스 제공 및 개선
              <br />ㆍ{branding.nameCompact} 기본 서비스(건강관리 프로그램 등)
              제공 및 개선
              <br />
              ㆍ신규 서비스 개발 및 통계 분석
              <br />
              ㆍ고객 문의 응대 및 문제 해결
              <br />
              <br />
              - 제공하는 정보 항목: 혈당 수치, 측정 시간
              <br />- 제공받는 자의 보유 및 이용 기간: {
                branding.nameCompact
              }{' '}
              서비스 해지 시까지 보유하며, 관련 법령에 따른 보관 기간이 남아있는
              경우 해당 기간 동안 보관
              <br />
              - 동의를 거부할 권리 및 불이익: 본 동의를 거부할 권리가 있으며,
              거부할 경우 서비스 이용에 제한이 있을 수 있습니다.
              <br />
              <br />본 동의는 슈가핏 서비스를 이용하는 데 필요한 필수 사항으로,
              민감정보의 제3자 제공에 대해 동의하시는 경우 아래 확인란에 체크해
              주시기 바랍니다.
            </OptionInnerContent>
          </OptionContent>
        );
      default:
        return null;
    }
  };

  // 확장 기능
  const [expandedItems, setExpandedItems] = useState({});
  const [rotatedItems, setRotatedItems] = useState({});

  const toggleExpand = (id) => {
    setExpandedItems((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
    setRotatedItems((prev) => ({
      ...prev,
      [id]: prev[id] === 'right' ? 'left' : 'right',
    }));
  };

  if (!isReady) {
    return (
      <Layout>
        <Container2 />
      </Layout>
    );
  }

  return (
    <div>
      <Layout>
        <Container2>
          <Container>
            <Title2>서비스 제공을 위한 이용 동의</Title2>
            {/* {JSON.stringify(allAgreed)}
          {JSON.stringify(agreements)} */}
            <OptionItem onClick={handleAllAgree}>
              <CheckMark agreed={allAgreed} /> 약관 전체 동의
            </OptionItem>
            <Separator />
            <OptionList>
              {options.map((option) => (
                <OptionContainer key={option.id}>
                  <OptionItem>
                    <CheckMark
                      onClick={() => {
                        handleAgreementChange(option.id);
                      }}
                      agreed={agreements[option.id]}
                    />
                    <OptionText>{option.text}</OptionText>
                    <RotatingImage
                      src="https://glucofit-image.s3.ap-northeast-2.amazonaws.com/LOG/chevron-right.svg"
                      alt="Option icon"
                      onClick={() => toggleExpand(option.id)}
                      isRotated={!!rotatedItems[option.id]}
                      direction={rotatedItems[option.id]}
                    />
                  </OptionItem>
                  {renderContent(option.id)}
                </OptionContainer>
              ))}
            </OptionList>
            <Button
              disabled={!allAgreed}
              allAgreed={allAgreed}
              onClick={() => handleSubmit()}
            >
              동의
            </Button>
          </Container>
        </Container2>
      </Layout>
    </div>
  );
};

export default AnyfitAgreement;
